import { useEffect, useId, useRef, useState, type KeyboardEventHandler, type ReactNode } from "react";
import { Glyph } from "./icons";
import "./overlays.css";

/** Native modal focus/inert handling, with controlled feature state and no business rules. */
export function Dialog({ open, onClose, title, description, children, footer, busy = false, size = "medium", fullscreen = false, expandable = false, dismissOnBackdrop = false, onKeyDown }: {
  open: boolean; onClose: () => void; title: ReactNode; description?: string; children: ReactNode; footer?: ReactNode;
  busy?: boolean; fullscreen?: boolean; size?: "small" | "medium" | "large" | "wide";
  /** Adds a header control that lets the person toggle the dialog to fill the whole screen. */
  expandable?: boolean; dismissOnBackdrop?: boolean;
  onKeyDown?: KeyboardEventHandler<HTMLDialogElement>;
}) {
  const [full, setFull] = useState(false);
  const ref = useRef<HTMLDialogElement>(null);
  const backdropPress = useRef(false);
  const invoker = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      invoker.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      dialog.showModal();
    }
    if (!open && dialog.open) {
      dialog.close();
      if (invoker.current?.isConnected) invoker.current.focus();
    }
    return () => {
      if (dialog.open) dialog.close();
      if (open && invoker.current?.isConnected) invoker.current.focus();
    };
  }, [open]);
  return <dialog ref={ref} aria-labelledby={titleId} aria-describedby={description ? descriptionId : undefined}
    className={`workspace-dialog purpose-card workspace-dialog--${full ? "full" : size}`} aria-busy={busy || undefined} data-fullscreen={fullscreen || undefined} onKeyDown={onKeyDown}
    onPointerDown={event => {
      const r = event.currentTarget.getBoundingClientRect();
      backdropPress.current = event.target === event.currentTarget && (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom);
    }}
    onClick={event => {
      const r = event.currentTarget.getBoundingClientRect();
      if (dismissOnBackdrop && !busy && backdropPress.current && event.target === event.currentTarget && (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom)) onClose();
      backdropPress.current = false;
    }}
    onCancel={(event) => { event.preventDefault(); event.stopPropagation(); if (!busy) onClose(); }}>
    <div className="workspace-dialog-header">
      <div><h2 id={titleId}>{title}</h2>{description && <p id={descriptionId}>{description}</p>}</div>
      <div className="workspace-dialog-controls">
      {expandable && <button type="button" className="icon-button" aria-label={full ? "Exit full screen" : "Expand to full screen"} aria-pressed={full} onClick={() => setFull(value => !value)}><Glyph name={full ? "close_fullscreen" : "open_in_full"} size={18} /></button>}
      <button type="button" className="icon-button" aria-label="Close dialog" disabled={busy} onClick={onClose}><Glyph name="close" size={18} /></button></div>
    </div>
    <div className="workspace-dialog-body">{children}</div>
    {footer && <div className="workspace-dialog-footer">{footer}</div>}
  </dialog>;
}
